import React, { ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { ICollaboratorViewModel } from 'viewModels';
import { useQuery } from 'react-query';
import { CompanyController } from '../controller/CompanyController';
import { useAuth } from './auth';
import useWebSocket from '../hooks/useWebSocket';

type CollaboratorContextProps = {
  children: ReactNode;
};
interface IInitialValues {
  collaborators: ICollaboratorViewModel[];
  setCollaborators: React.Dispatch<React.SetStateAction<ICollaboratorViewModel[]>>;
  setEnableCollaboratorsLoad: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingCollaborators: boolean;
}
const CollaboratorContext = React.createContext({} as IInitialValues);

const CollaboratorProvider = ({ children }: CollaboratorContextProps) => {
  const [enableCollaboratorsLoad, setEnableCollaboratorsLoad] = useState<boolean>(false);
  const { user, tokenIsValid } = useAuth();
  const [collaborators, setCollaborators] = useState<ICollaboratorViewModel[]>([]);

  const { isLoading: isLoadingCollaborators } = useQuery(
    'collaborator',
    () => CompanyController.getCollaboratorsByCompanyId(user.id),
    {
      onSuccess: (data) => {
        setCollaborators(data);
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: !!user.id && tokenIsValid && enableCollaboratorsLoad,
    },
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setCollaborators([]);
    }
  }, [tokenIsValid]);

  useWebSocket<ICollaboratorViewModel>({
    url: `collaborator/${user.id}/update-collaborator`,
    onMessage: (collaborator) => {
      setCollaborators((prev) => {
        const index = prev.findIndex((c) => c.id === collaborator.id);
        if (index !== -1) {
          return [...prev.slice(0, index), collaborator, ...prev.slice(index + 1)];
        }
        return [...prev];
      });
    },
  });
  useWebSocket<ICollaboratorViewModel>({
    url: `collaborator/${user.id}/transfer-collaborator`,
    onMessage: (collaborator) => {
      setCollaborators((prev) => {
        const index = prev.findIndex((c) => c.id === collaborator.id);
        if (index !== -1) {
          return [...prev.slice(0, index), collaborator, ...prev.slice(index + 1)];
        }
        return [...prev];
      });
    },
  });

  const contextValues = useMemo(
    () => ({ collaborators, setCollaborators, setEnableCollaboratorsLoad, isLoadingCollaborators }),
    [collaborators, isLoadingCollaborators],
  );
  return (
    <CollaboratorContext.Provider value={contextValues}>{children}</CollaboratorContext.Provider>
  );
};
export const useCollaborator = () => useContext(CollaboratorContext);
export default CollaboratorProvider;
